import { useEffect, useState } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { Star, Gift, Users, TrendingUp, Crown } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LangContext";
import { AnimatedEmblem } from "../components/shop/AnimatedEmblem";
import { JoinHero } from "../components/community/JoinParts";
import { Stepper, StepAccount, StepProfile, StepAvatar, StepComplete } from "../components/community/JoinSteps";

const Welcome = ({ points }) => {
  const { tr } = useLang();
  const nav = useNavigate();
  const items = [[Star, tr("Punkte sammeln", "Start earning Jolgen points")], [Gift, tr("Exklusive Member-Deals", "Access member deals")], [Users, tr("Community-Events", "Join community events")], [TrendingUp, tr("Sammlerprofil leveln", "Level up your profile")]];
  return (
    <section className="bg-[#120E1F] grain relative overflow-hidden min-h-[80vh] grid place-items-center px-4 py-20 text-center" data-testid="join-welcome">
      <div className="psychic-glow float absolute top-0 left-1/2 -ml-[300px] h-[600px] w-[600px] rounded-full" />
      <div className="relative space-y-8 max-w-xl">
        <div className="relative mx-auto h-40 w-40"><div className="absolute inset-0 rounded-full bg-[radial-gradient(circle,#FFF6C9_0%,rgba(242,194,48,.5)_35%,transparent_70%)] blur-xl animate-pulse" /><AnimatedEmblem className="relative h-40 w-40" testid="welcome-emblem" /></div>
        <h1 className="font-display text-5xl sm:text-6xl text-white">{tr("Willkommen bei", "Welcome to")} <span className="text-gold-grad">Jolgen Cards!</span></h1>
        <p className="flex items-center justify-center gap-2 text-[#C9B6F2]"><Crown className="h-4 w-4 text-[#F2C230]" />{tr("Du bist jetzt Jolgen Cards-Mitglied", "You are now a Jolgen Cards member")} · <span className="font-mono text-[#F2C230]" data-testid="welcome-points">{points} {tr("Punkte", "points")}</span></p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">{items.map(([I, l]) => <div key={l} className="space-y-2"><span className="mx-auto h-14 w-14 rounded-full grid place-items-center bg-[#8A5CD6]/20 border border-[#8A5CD6]/50"><I className="h-6 w-6 text-[#F2C230]" /></span><p className="text-xs text-white/70">{l}</p></div>)}</div>
        <button onClick={() => nav("/member")} className="btn-gold w-full sm:w-auto sm:px-14 h-12" data-testid="welcome-go-profile">{tr("Zu meinem Profil", "Go to my profile")}</button>
      </div>
    </section>
  );
};

export default function Join() {
  const { user, register, refresh } = useAuth();
  const [params] = useSearchParams();
  const [started, setStarted] = useState(false);
  const [step, setStep] = useState(0);
  const [me, setMe] = useState({});
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (user && !done) { api.get("/community/me").then((r) => { setMe(r.data); setStep((s) => Math.max(s, 1)); setStarted(true); }); }
  }, [user, done]);

  if (user === null) return <div className="min-h-[60vh]" />;
  if (user && user.onboarded && !done) return <Navigate to="/member" />;
  if (done) return <Welcome points={me.points} />;
  if (!user && !started) return <JoinHero onStart={() => setStarted(true)} />;

  const run = async (fn) => { setBusy(true); try { await fn(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); } };
  const saveProfile = (body, next) => run(async () => { const { data } = await api.put("/community/profile", body); setMe(data); if (next) setStep(next); else { await refresh(); setDone(true); } });
  const createAccount = (f) => run(async () => { await register({ email: f.email, password: f.password, ref: params.get("ref") || undefined }); });

  return (
    <div className="bg-[#F7F5FB] min-h-[80vh] py-12 px-4">
      <div className="max-w-xl mx-auto bg-white rounded-[28px] border border-[#120E1F]/5 shadow-[0_30px_60px_-30px_rgba(110,63,184,0.35)] p-6 sm:p-10 space-y-8">
        <Stepper step={step} />
        {step === 0 && <StepAccount onSubmit={createAccount} busy={busy} />}
        {step === 1 && <StepProfile initial={me} busy={busy} onSubmit={(b) => saveProfile(b, 2)} />}
        {step === 2 && <StepAvatar initial={me.avatar} busy={busy} onSubmit={(b) => saveProfile(b, 3)} />}
        {step === 3 && <StepComplete initial={me} busy={busy} onSubmit={(b) => saveProfile(b, null)} />}
      </div>
    </div>
  );
}
